// PlayerControls.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { format } from 'date-fns';

import { getTimeFormat } from '../resource/playerUtils.js';
import Seekbar from '../component/Seekbar.jsx';

import AudioOffIcon from '../../img/ic-audio-off.svg';
import AudioOnIcon from '../../img/ic-audio-on.svg';
import FullscreenIcon from '../../img/ic-fullscreen-20.svg';
import ExitFullscreenIcon from '../../img/ic-exit-fullscreen-20.svg';
import PlayIcon from '../../img/ic-play-white.svg';
import PauseIcon from '../../img/ic-pause-white.svg';
import ReplayIcon from '../../img/ic-replay.svg';
import { ButtonId } from '../resource/mixpanel.js';

const stopPropagation = e => e.stopPropagation();

export const PlayerControls = ({
  bufferedTime,
  currentTime,
  duration,
  isFullscreen,
  isMuted,
  isPaused,
  isEnded,
  showMute,
  showPlay,
  showSeeker,
  showFullscreen,
  onHover,
  onAudioClick,
  onFullscreenClick,
  onPlayClick,
  onSeekChange,
  onSeekStart,
  onSeekEnd,
  trackingPayload,
}) => {
  let currentTimeString;
  let remainingTimeString;
  if (Number.isFinite(duration)) {
    // https://github.com/date-fns/date-fns/issues/3117
    const d = new Date();
    const timezoneOffsetInSeconds = d.getTimezoneOffset() * 60; // seconds
    currentTimeString = format(
      (currentTime + timezoneOffsetInSeconds) * 1000,
      getTimeFormat(duration)
    );
    remainingTimeString = format(
      (duration - currentTime + timezoneOffsetInSeconds) * 1000,
      getTimeFormat(duration)
    );
  } else {
    currentTimeString = format(currentTime * 1000, 'mm:ss');
    remainingTimeString = '--:--';
  }

  return (
    <StyledPlayerControls
      data-click_tracking_disabled
      onMouseEnter={() => {
        onHover({ isHovered: true });
      }}
      onMouseLeave={() => {
        onHover({ isHovered: false });
      }}
      onClick={stopPropagation}
    >
      {showPlay && (
        <ActionButton
          key="play"
          data-element_id={
            isPaused
              ? ButtonId.Detail.PlayerControlsPlay
              : ButtonId.Detail.PlayerControlsPause
          }
          data-tracking_payload={trackingPayload}
          onClick={onPlayClick}
        >
          <img
            src={isEnded ? ReplayIcon : isPaused ? PlayIcon : PauseIcon}
            alt="play"
          />
        </ActionButton>
      )}
      {showSeeker && (
        <SeekbarBlock>
          <Time>{currentTimeString}</Time>
          <SeekbarWrapper>
            <Seekbar
              value={currentTime}
              max={duration}
              bufferedTime={bufferedTime}
              onChange={onSeekChange}
              onSeekStart={onSeekStart}
              onSeekEnd={onSeekEnd}
            />
          </SeekbarWrapper>
          <Time>{remainingTimeString}</Time>
        </SeekbarBlock>
      )}
      {showMute && (
        <ActionButton
          key="audio"
          data-element_id={
            isMuted ? ButtonId.All.ButtonMuted : ButtonId.All.ButtonVolume
          }
          data-tracking_payload={trackingPayload}
          onClick={onAudioClick}
        >
          <img src={isMuted ? AudioOffIcon : AudioOnIcon} alt="muted" />
        </ActionButton>
      )}
      {showFullscreen && (
        <ActionButton
          key="fullscreen"
          data-element_id={ButtonId.Detail.PlayerControlsFullscreen}
          data-tracking_payload={trackingPayload}
          onClick={onFullscreenClick}
        >
          <img
            src={isFullscreen ? ExitFullscreenIcon : FullscreenIcon}
            alt="fullscreen"
          />
        </ActionButton>
      )}
    </StyledPlayerControls>
  );
};

PlayerControls.propTypes = {
  bufferedTime: PropTypes.number,
  currentTime: PropTypes.number,
  duration: PropTypes.number,
  showMute: PropTypes.bool,
  showPlay: PropTypes.bool,
  showSeeker: PropTypes.bool,
  showFullscreen: PropTypes.bool,
  isFullscreen: PropTypes.bool,
  isMuted: PropTypes.bool,
  isPaused: PropTypes.bool,
  isEnded: PropTypes.bool,
  onHover: PropTypes.func,
  onAudioClick: PropTypes.func,
  onFullscreenClick: PropTypes.func,
  onPlayClick: PropTypes.func,
  onSeekChange: PropTypes.func,
  onSeekStart: PropTypes.func,
  onSeekEnd: PropTypes.func,
  trackingPayload: PropTypes.object,
};

PlayerControls.defaultProps = {
  bufferedTime: 0,
  currentTime: 0,
  duration: 0,
  isFullscreen: false,
  isMuted: false,
  isPaused: false,
  isEnded: false,
  showMute: false,
  showPlay: false,
  showSeeker: false,
  showFullscreen: false,
  onHover: null,
  onAudioClick: null,
  onFullscreenClick: null,
  onPlayClick: null,
  onSeekChange: null,
  onSeekStart: null,
  onSeekEnd: null,
  trackingPayload: {},
};

const StyledPlayerControls = styled.div`
  padding: 4px 8px;
  display: flex;
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  background: none;
  padding: 8px;
  border: 0;
  outline: 0;

  & > img {
    width: 20px;
    height: 20px;
    display: block;
  }
`;

const SeekbarBlock = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
`;

const SeekbarWrapper = styled.div`
  margin-left: 10px;
  margin-right: 10px;
  flex: 1;
`;

const Time = styled.div`
  box-sizing: content-box;
  margin-left: auto;
  color: #fff;
  font-size: 11px;
  line-height: 1;
`;

export default PlayerControls;
